import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserGroupIcon } from '@heroicons/react/24/outline';
import { useAdminUsers, useCreateUser } from '@/hooks/useAdmin';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { SearchInput } from '@/components/ui/SearchInput';
import { Pagination } from '@/components/ui/Pagination';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { formatRelativeTime } from '@/utils/format';

const roleVariant: Record<string, 'purple' | 'info' | 'default'> = { super_admin: 'purple', admin: 'info', user: 'default', store_owner: 'info', manager: 'default', staff: 'default' };
const statusVariant: Record<string, 'success' | 'warning' | 'error'> = { active: 'success', suspended: 'warning', inactive: 'error' };

const INITIAL_FORM = {
  email: '', firstName: '', lastName: '', password: '', role: 'store_owner',
  phone: '', company: '', notes: '',
  plan: 'yearly', maxSites: 3, priceAmount: 0, expiresAt: '',
};

export function AdminUsersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data, isLoading, isError, refetch } = useAdminUsers({ page, perPage, search, role: roleFilter, status: statusFilter });
  const createUser = useCreateUser();

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = t('common.required');
    if (!form.firstName.trim()) errs.firstName = t('common.required');
    if (!form.lastName.trim()) errs.lastName = t('common.required');
    if (!form.password || form.password.length < 8) errs.password = t('settings.passwordMinLength');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleCreate = async () => {
    if (!validate()) return;
    await createUser.mutateAsync({
      email: form.email, password: form.password,
      firstName: form.firstName, lastName: form.lastName,
      role: form.role, phone: form.phone || undefined, company: form.company || undefined,
      notes: form.notes || undefined,
      subscription: {
        plan: form.plan,
        maxSites: form.maxSites,
        priceAmount: form.priceAmount || undefined,
        expiresAt: form.expiresAt || undefined,
      },
    });
    setCreateOpen(false);
    setForm(INITIAL_FORM);
    setErrors({});
  };

  if (isError) return <ErrorState onRetry={() => refetch()} />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('admin.users')}</h1>
        <Button onClick={() => setCreateOpen(true)}>{t('admin.createUser')}</Button>
      </div>

      <Card>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          <SearchInput value={search} onChange={setSearch} placeholder={t('common.search')} />
          <Select options={[{ value: '', label: t('common.all') }, { value: 'super_admin', label: t('roles.super_admin') }, { value: 'store_owner', label: t('roles.store_owner') }, { value: 'staff', label: t('roles.staff') }]} value={roleFilter} onChange={setRoleFilter} />
          <Select options={[{ value: '', label: t('common.all') }, { value: 'active', label: t('common.active') }, { value: 'suspended', label: t('common.suspended') }, { value: 'inactive', label: t('common.inactive') }]} value={statusFilter} onChange={setStatusFilter} />
        </div>

        {isLoading ? (
          <div className="space-y-3">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}</div>
        ) : data?.items?.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    <th className="px-4 py-2"></th>
                    <th className="px-4 py-2">{t('common.name')}</th>
                    <th className="px-4 py-2">{t('auth.email')}</th>
                    <th className="px-4 py-2">{t('common.role')}</th>
                    <th className="px-4 py-2">{t('common.status')}</th>
                    <th className="px-4 py-2">{t('subscription.plan')}</th>
                    <th className="px-4 py-2">{t('common.expires')}</th>
                    <th className="px-4 py-2">{t('common.created')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((u: { id: string; firstName: string; lastName: string; email: string; role: string; status: string; avatarUrl?: string; createdAt: string; subscription?: { plan: string; expiresAt?: string } }) => (
                    <tr key={u.id} className="cursor-pointer border-b border-gray-50 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/50" onClick={() => navigate(`/admin/users/${u.id}`)}>
                      <td className="px-4 py-3"><Avatar name={`${u.firstName} ${u.lastName}`} src={u.avatarUrl} size="sm" /></td>
                      <td className="px-4 py-3 font-medium text-brand-600 dark:text-brand-400">{u.firstName} {u.lastName}</td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{u.email}</td>
                      <td className="px-4 py-3"><Badge label={t(`roles.${u.role}`, { defaultValue: u.role })} variant={roleVariant[u.role] ?? 'default'} /></td>
                      <td className="px-4 py-3"><Badge label={t(`common.${u.status}`)} variant={statusVariant[u.status] ?? 'default'} /></td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{u.subscription?.plan ? t(`subscription.plans.${u.subscription.plan}`, { defaultValue: u.subscription.plan }) : '-'}</td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{u.subscription?.expiresAt ? formatRelativeTime(u.subscription.expiresAt) : '-'}</td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{formatRelativeTime(u.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.totalPages > 1 && <div className="mt-4 px-4"><Pagination page={data.page} perPage={data.perPage} total={data.total} totalPages={data.totalPages} onPageChange={setPage} onPerPageChange={(pp) => { setPerPage(pp); setPage(1); }} /></div>}
          </>
        ) : (
          <EmptyState icon={UserGroupIcon} title={search ? t('common.noResults') : t('admin.noUsers')} />
        )}
      </Card>

      <Modal isOpen={createOpen} onClose={() => { setCreateOpen(false); setErrors({}); }} title={t('admin.createUser')} size="lg">
        <div className="space-y-4">
          <Input label={t('auth.email')} type="email" value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} error={errors.email} />
          <div className="grid grid-cols-2 gap-3">
            <Input label={t('settings.firstName')} value={form.firstName} onChange={(e) => setForm((s) => ({ ...s, firstName: e.target.value }))} error={errors.firstName} />
            <Input label={t('settings.lastName')} value={form.lastName} onChange={(e) => setForm((s) => ({ ...s, lastName: e.target.value }))} error={errors.lastName} />
          </div>
          <Input label={t('auth.password')} type="password" value={form.password} onChange={(e) => setForm((s) => ({ ...s, password: e.target.value }))} error={errors.password} />
          <Select label={t('common.role')} options={[{ value: 'store_owner', label: t('roles.store_owner') }, { value: 'manager', label: t('roles.manager') }, { value: 'staff', label: t('roles.staff') }]} value={form.role} onChange={(v) => setForm((s) => ({ ...s, role: v }))} />
          <div className="grid grid-cols-2 gap-3">
            <Input label={t('common.phone')} value={form.phone} onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))} />
            <Input label={t('common.company')} value={form.company} onChange={(e) => setForm((s) => ({ ...s, company: e.target.value }))} />
          </div>

          <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
            <p className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">{t('subscription.title')}</p>
            <div className="grid grid-cols-2 gap-3">
              <Select label={t('subscription.plan')} options={[{ value: 'free_trial', label: t('subscription.plans.free_trial') }, { value: 'monthly', label: t('subscription.plans.monthly') }, { value: 'yearly', label: t('subscription.plans.yearly') }, { value: 'lifetime', label: t('subscription.plans.lifetime') }, { value: 'custom', label: t('subscription.plans.custom') }]} value={form.plan} onChange={(v) => setForm((s) => ({ ...s, plan: v }))} />
              <Input label={t('subscription.maxSites')} type="number" min="1" value={String(form.maxSites)} onChange={(e) => setForm((s) => ({ ...s, maxSites: Number(e.target.value) || 1 }))} />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <Input label={t('common.price')} type="number" min="0" step="0.01" value={String(form.priceAmount)} onChange={(e) => setForm((s) => ({ ...s, priceAmount: Number(e.target.value) || 0 }))} />
              <Input label={t('common.expires')} type="date" value={form.expiresAt} onChange={(e) => setForm((s) => ({ ...s, expiresAt: e.target.value }))} />
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
            <Button variant="secondary" onClick={() => { setCreateOpen(false); setErrors({}); }}>{t('common.cancel')}</Button>
            <Button onClick={handleCreate} loading={createUser.isPending}>{t('common.save')}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
